import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/Layouts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { StatusBadge } from "@/components/StatusBadge";
import { VideoThumbnail } from "@/components/VideoThumbnail";
import { ThumbnailUploader } from "@/components/ThumbnailUploader";
import { VimeoUrlField } from "@/components/VimeoUrlField";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Edit, Plus, Trash2 } from "lucide-react";
import { BatchAccessField } from "@/components/BatchAccessField";
import api, { thumbUrl } from "@/lib/api";
import { isVimeo, isYouTube } from "@/lib/videoUrl";
import { toast } from "sonner";

function videoSourceLabel(url) {
  if (!url) return { label: "None", tone: "muted" };
  if (isVimeo(url)) return { label: "Vimeo", tone: "teal" };
  if (isYouTube(url)) return { label: "YouTube", tone: "teal" };
  if (/^\/api\/files\//.test(url) || /^https?:\/\/.*\/api\/files\//.test(url)) return { label: "Internal File", tone: "teal" };
  return { label: "URL", tone: "teal" };
}

const EMPTY_RECORDING = {
  id: null,
  title: "",
  description: "",
  video_url: "",
  thumbnail_image: "",
  status: "belum_dimuat_naik",
  published: false,
  visibility: "all_students",
  allowed_batches: [],
};

export default function AdminRecordings() {
  const [items, setItems] = useState([]);
  const [batches, setBatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/recordings").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/admin/batches").then((r) => setBatches(r.data)).catch(() => setBatches([]));
  }, []);

  const openEdit = (r) => {
    setEditing({
      ...r,
      visibility: r.visibility || "all_students",
      allowed_batches: r.allowed_batches || [],
    });
    setOpen(true);
  };
  const openCreate = () => { setEditing({ ...EMPTY_RECORDING }); setOpen(true); };

  const save = async () => {
    if (!editing.title || !editing.title.trim()) {
      toast.error("Please isi tajuk sesi.");
      return;
    }
    try {
      const payload = {
        title: editing.title,
        description: editing.description,
        video_url: editing.video_url,
        thumbnail_image: editing.thumbnail_image,
        status: editing.status,
        published: editing.published,
        visibility: editing.visibility || "all_students",
        allowed_batches: editing.allowed_batches || [],
      };
      if (editing.id) {
        await api.patch(`/admin/recordings/${editing.id}`, payload);
        toast.success("Recording updated");
      } else {
        await api.post("/admin/recordings", payload);
        toast.success("Recording added");
      }
      setOpen(false);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to save";
      toast.error(typeof msg === "string" ? msg : "Failed to save");
    }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete recording "${title}"? This action cannot be undone.`)) return;
    try {
      await api.delete(`/admin/recordings/${id}`);
      toast.success("Recording deleted");
      load();
    } catch { toast.error("Gagal padam"); }
  };

  return (
    <AdminLayout title="Class Recordings">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Class Recordings</h1>
          <p className="mt-1 text-muted-foreground">Manage class recordings, Vimeo URLs, and thumbnails.</p>
        </div>
        <Button onClick={openCreate} data-testid="add-recording-btn" className="btn-glow">
          <Plus className="h-4 w-4" /> Add New Recording
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((r) => (
          <Card key={r.id} className="glass-card overflow-hidden" data-testid={`admin-rec-${r.id}`}>
            <VideoThumbnail thumbnailUrl={thumbUrl(r.thumbnail_image, r.updated_at)} hasVideo={!!r.video_url} status={r.status} />
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-heading font-semibold">{r.title}</h3>
                <StatusBadge status={r.status} />
              </div>
              <div className="text-xs space-y-1">
                <div className="flex items-center gap-1.5">
                  Sumber Video:
                  <span className={r.video_url ? `text-${videoSourceLabel(r.video_url).tone}` : "text-muted-foreground"}>
                    {videoSourceLabel(r.video_url).label}
                  </span>
                </div>
                <div>Thumbnail: <span className={r.thumbnail_image ? "text-teal" : "text-muted-foreground"}>{r.thumbnail_image ? "Has thumbnail" : "No thumbnail"}</span></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={() => openEdit(r)} data-testid={`admin-rec-edit-${r.id}`}><Edit className="h-3.5 w-3.5" /> Edit</Button>
                <Button variant="destructive" onClick={() => remove(r.id, r.title)} data-testid={`admin-rec-delete-${r.id}`}>
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {editing && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing.id ? `Edit ${editing.title}` : "Add New Recording"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div><Label>Session Title</Label><Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
              <div><Label>Description</Label><Textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>

              <div className="rounded-lg border border-border p-4 bg-muted/20 space-y-4">
                <div>
                  <p className="text-sm font-medium">Sumber Video</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Choose one: paste a Vimeo link, or upload a video file.
                  </p>
                </div>
                <VimeoUrlField
                  value={editing.video_url}
                  onChange={(v) => setEditing({ ...editing, video_url: v })}
                  testId="rec-vimeo-field"
                />
                <div className="text-center text-[10px] uppercase tracking-wider text-muted-foreground">— or —</div>
                <ThumbnailUploader
                  value={editing.video_url}
                  onChange={(v) => setEditing({ ...editing, video_url: v })}
                  kind="video"
                  label="Upload video file from PC"
                  testId="rec-video-uploader"
                />
              </div>

              <ThumbnailUploader value={editing.thumbnail_image} onChange={(v) => setEditing({ ...editing, thumbnail_image: v })} kind="thumbnail" label="Thumbnail Image" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Status</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                    <SelectTrigger data-testid="rec-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="belum_dimuat_naik">Not uploaded yet</SelectItem>
                      <SelectItem value="tersedia">Available</SelectItem>
                      <SelectItem value="disembunyikan">Hidden</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <Switch checked={editing.published} onCheckedChange={(v) => setEditing({ ...editing, published: v })} id="pub" data-testid="rec-published" />
                  <Label htmlFor="pub">Published</Label>
                </div>
              </div>

              <BatchAccessField
                visibility={editing.visibility}
                allowedBatches={editing.allowed_batches}
                batches={batches}
                onChange={({ visibility, allowed_batches }) => setEditing({ ...editing, visibility, allowed_batches })}
                contentLabel="recording"
                testId="rec-batch-access"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} data-testid="save-rec-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </AdminLayout>
  );
}
